import React from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { Group, Search, TokenOutlined } from "@mui/icons-material";
import moment from "moment";
import { GlobalContext } from "../../context/GlobalContext";
import UpdateCredit from "./components/UpdateCredit";

const BRAND = "#6D28D9";

function parsePlan(plan) {
  if (!plan) return null;
  if (typeof plan === "object") return plan;
  try {
    return JSON.parse(plan);
  } catch {
    return null;
  }
}

function planTitle(user) {
  const p = parsePlan(user?.plan);
  return p?.title || p?.name || (user?.plan ? "Plan" : "Free");
}

function initials(name, email) {
  const s = String(name || email || "?").trim();
  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase();
}

function KpiCard({ label, value, hint }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
        height: "100%",
      }}
    >
      <Typography variant="caption" color="text.secondary" fontWeight={600}>
        {label}
      </Typography>
      <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5, letterSpacing: -0.5 }}>
        {value}
      </Typography>
      {hint ? (
        <Typography variant="caption" color="text.disabled">
          {hint}
        </Typography>
      ) : null}
    </Paper>
  );
}

function navigateAdmin(page, extra = {}) {
  const params = new URLSearchParams(window.location.search);
  params.set("page", page);
  Object.entries(extra).forEach(([k, v]) => {
    if (v == null || v === "") params.delete(k);
    else params.set(k, String(v));
  });
  if (page !== "user-profile") params.delete("uid");
  const next = `${window.location.pathname}?${params.toString()}`;
  window.history.pushState({ path: next }, "", next);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

const Users = ({ lang }) => {
  const theme = useTheme();
  const { hitAxios } = React.useContext(GlobalContext);
  const [users, setUsers] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [summary, setSummary] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState("");
  const [searchInput, setSearchInput] = React.useState("");
  const [planFilter, setPlanFilter] = React.useState("all");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [creditUser, setCreditUser] = React.useState(null);

  const fetchUsers = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page + 1),
        limit: String(rowsPerPage),
      });
      if (search.trim()) params.set("q", search.trim());
      if (planFilter !== "all") params.set("plan", planFilter);
      const res = await hitAxios({
        path: `/api/admin/get_users?${params.toString()}`,
        post: false,
        admin: true,
        showLoading: false,
      });
      if (res?.data?.success) {
        setUsers(Array.isArray(res.data.data) ? res.data.data : []);
        setTotal(Number(res.data.total || 0));
        setSummary(res.data.summary || null);
      }
    } finally {
      setLoading(false);
    }
  }, [hitAxios, page, rowsPerPage, search, planFilter]);

  React.useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  React.useEffect(() => {
    const t = window.setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 350);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const planOptions = React.useMemo(() => {
    const set = new Set(users.map((u) => planTitle(u)));
    if (planFilter !== "all") set.add(planFilter);
    return Array.from(set).sort();
  }, [users, planFilter]);

  const kpis = {
    total: summary?.total_users ?? total,
    activeWeek: summary?.active_this_week ?? 0,
    activation:
      summary?.total_users > 0
        ? Math.round((Number(summary.active_this_week || 0) / summary.total_users) * 100)
        : 0,
    paid: summary?.paid_plans ?? 0,
    flagged: summary?.flagged ?? 0,
  };

  return (
    <Box>
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="h5" fontWeight={800}>
          {lang?.usersAccessTitle || "Users & Access"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {lang?.usersAccessSub || "Manage accounts, plans, credits, and access across the platform."}
        </Typography>
      </Box>

      <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard label={lang?.totalUsers || "Total Users"} value={kpis.total} />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard
            label={lang?.activeThisWeek || "Joined This Week"}
            value={kpis.activeWeek}
            hint={`${kpis.activation}% of total`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard label={lang?.paidPlans || "Paid Plans"} value={kpis.paid} />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard label={lang?.shownOnPage || "On this page"} value={users.length} />
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <KpiCard label={lang?.flaggedAccounts || "Flagged Accounts"} value={kpis.flagged} />
        </Grid>
      </Grid>

      <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2, overflow: "hidden" }}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.25}
          sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}
          alignItems={{ md: "center" }}
        >
          <TextField
            size="small"
            placeholder={lang?.searchNameEmail || "Search name or email"}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: { md: 280 } }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>{lang?.allPlans || "All plans"}</InputLabel>
            <Select
              label={lang?.allPlans || "All plans"}
              value={planFilter}
              onChange={(e) => {
                setPlanFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="all">{lang?.allPlans || "All plans"}</MenuItem>
              {planOptions.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            size="small"
            onClick={() => {
              setSearchInput("");
              setSearch("");
              setPlanFilter("all");
              setPage(0);
            }}
            sx={{ textTransform: "none" }}
          >
            {lang?.clear || "Clear"}
          </Button>
          <Box sx={{ flex: 1 }} />
          <Chip icon={<Group />} label={`${total} total`} size="small" variant="outlined" />
        </Stack>

        <TableContainer sx={{ maxHeight: 620 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>{lang?.user || "User"}</TableCell>
                <TableCell>{lang?.plan || "Plan"}</TableCell>
                <TableCell>{lang?.credits || "Credits"}</TableCell>
                <TableCell>{lang?.joined || "Joined"}</TableCell>
                <TableCell>{lang?.status || "Status"}</TableCell>
                <TableCell align="right">{lang?.actions || "Actions"}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      Loading…
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      No users match these filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((u, idx) => {
                  const title = planTitle(u);
                  const status = String(u.status || "active").toLowerCase();
                  const credits = Number(u.credits ?? 0);
                  return (
                    <TableRow key={u.uid} hover>
                      <TableCell>{page * rowsPerPage + idx + 1}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1.25} alignItems="center">
                          <Avatar
                            sx={{
                              width: 36,
                              height: 36,
                              bgcolor: alpha(BRAND, 0.15),
                              color: BRAND,
                              fontSize: 13,
                              fontWeight: 800,
                            }}
                          >
                            {initials(u.name, u.email)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight={700}>
                              {u.name || "—"}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {u.email}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={`• ${title}`}
                          sx={{ bgcolor: alpha(theme.palette.grey[500], 0.12), fontWeight: 700 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          icon={<TokenOutlined sx={{ fontSize: "14px !important" }} />}
                          label={credits.toLocaleString()}
                          sx={{
                            bgcolor: alpha(BRAND, 0.12),
                            color: BRAND,
                            fontWeight: 800,
                            "& .MuiChip-icon": { color: BRAND },
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {u.createdAt || u.created_at
                            ? moment(u.createdAt || u.created_at).format("D MMM YYYY")
                            : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={status === "active" ? "Active" : status}
                          color={status === "active" ? "success" : "warning"}
                          sx={{ fontWeight: 700 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => navigateAdmin("user-profile", { uid: u.uid })}
                            sx={{ textTransform: "none", fontWeight: 700 }}
                          >
                            {lang?.view || "View"}
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => setCreditUser(u)}
                            sx={{
                              textTransform: "none",
                              fontWeight: 700,
                              bgcolor: BRAND,
                              "&:hover": { bgcolor: alpha(BRAND, 0.9) },
                            }}
                          >
                            {lang?.credits || "Credits"}
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {creditUser ? (
        <UpdateCredit
          forceOpen
          onClose={() => setCreditUser(null)}
          lang={lang}
          hitAxios={hitAxios}
          hangleGetUsers={async () => {
            await fetchUsers();
            setCreditUser(null);
          }}
          params={{ row: creditUser, value: creditUser.credits }}
        />
      ) : null}
    </Box>
  );
};

export default Users;
